import { isSupportedKnowledgeCheckType } from '@/utils/knowledgeChecks.js'
import {
  licenseTypeIncludesKnowledgeChecks,
  modulesForLearnerEnrollment,
  seatLicenseTypeFromUserCourse,
} from '@/utils/courseLicenseAccess.js'

function normalizeKnowledgeCheckProgress(raw) {
  return Array.isArray(raw) ? raw : []
}

function progressModuleId(item) {
  return item?.moduleId ?? item?.module_id ?? null
}

function progressKnowledgeCheckId(item) {
  return item?.knowledgeCheckId ?? item?.knowledge_check_id ?? null
}

function progressIsComplete(item) {
  return item?.isComplete === true || item?.is_complete === true || item?.completed === true
}

/** @param {unknown[]} modules */
function videoIdsFromModules(modules) {
  return (Array.isArray(modules) ? modules : [])
    .flatMap((module) => (Array.isArray(module?.videos) ? module.videos : []))
    .map((video) => {
      if (video == null) return ''
      if (typeof video === 'string' || typeof video === 'number') return String(video)
      return video.id != null ? String(video.id) : ''
    })
    .filter((id) => id !== '')
}

/** @param {Record<string, unknown> | null | undefined} uc */
function completedVideoIds(uc) {
  return new Set(
    (Array.isArray(uc?.completed_videos) ? uc.completed_videos : [])
      .map((video) => {
        if (video == null) return null
        if (typeof video === 'string' || typeof video === 'number') return String(video)
        return video.id != null ? String(video.id) : null
      })
      .filter((id) => id != null && id !== '')
  )
}

/**
 * Projected completion % after a seat-license progress recalc.
 * Basic / Legacy / missing type: videos only.
 * Premium: videos + required knowledge checks.
 *
 * @param {unknown[]} modules
 * @param {Record<string, unknown> | null | undefined} userCourseSnap
 * @param {string | null | undefined} licenseType
 * @returns {number} 0–100 (fractional)
 */
/**
 * Synthetic learner snapshot: every video and (for Premium) knowledge check
 * on the given module list marked complete — used for “100% before update” examples.
 *
 * @param {unknown[]} modules
 * @param {string | null | undefined} licenseType
 * @returns {Record<string, unknown>}
 */
export function buildFullyCompletedUserCourseSnapshot(modules, licenseType) {
  const modList = Array.isArray(modules) ? modules : []
  const includeKnowledgeChecks = licenseTypeIncludesKnowledgeChecks(licenseType)
  const completed_modules = modList
    .map((module) => (module?.id != null ? module.id : null))
    .filter((id) => id != null)

  const snap = {
    completed_videos: videoIdsFromModules(modList),
    completed_modules,
    licenseBatch: licenseType ? { licenseType: String(licenseType) } : {},
  }

  if (includeKnowledgeChecks) {
    const knowledge_checks = []
    for (const module of modList) {
      if (!module || module.id == null) continue
      const raw = Array.isArray(module.knowledge_checks) ? module.knowledge_checks : []
      for (const kc of raw) {
        if (!kc || kc.active === false || !isSupportedKnowledgeCheckType(kc.type)) continue
        if (kc.id == null) continue
        knowledge_checks.push({
          moduleId: module.id,
          knowledgeCheckId: kc.id,
          isComplete: true,
        })
      }
    }
    snap.knowledge_checks = knowledge_checks
  }

  return snap
}

export function calculateProjectedPercentForLicenseType(modules, userCourseSnap, licenseType) {
  const uc = userCourseSnap && typeof userCourseSnap === 'object' ? userCourseSnap : {}
  const includeKnowledgeChecks = licenseTypeIncludesKnowledgeChecks(licenseType)
  const syntheticUc = includeKnowledgeChecks
    ? {
        ...uc,
        licenseBatch: {
          ...(uc.licenseBatch && typeof uc.licenseBatch === 'object' ? uc.licenseBatch : {}),
          ...(uc.license_batch && typeof uc.license_batch === 'object' ? uc.license_batch : {}),
          licenseType: licenseType ?? seatLicenseTypeFromUserCourse(uc),
        },
      }
    : uc

  const modList = modulesForLearnerEnrollment(modules, syntheticUc)
  const courseVideoIdSet = new Set(videoIdsFromModules(modList))
  const completedVideoSet = completedVideoIds(uc)
  const completedVideosInCourse = [...courseVideoIdSet].filter((id) =>
    completedVideoSet.has(id)
  ).length

  if (includeKnowledgeChecks) {
    let totalRequiredKnowledgeChecks = 0
    let completedRequiredKnowledgeChecks = 0
    for (const module of modList) {
      if (!module || module.id == null) continue
      const raw = Array.isArray(module.knowledge_checks) ? module.knowledge_checks : []
      const required = raw
        .map((kc, idx) => ({ kc, idx }))
        .filter(({ kc }) => kc && kc.active !== false && isSupportedKnowledgeCheckType(kc.type))
      totalRequiredKnowledgeChecks += required.length

      const checks = normalizeKnowledgeCheckProgress(uc?.knowledge_checks)
      completedRequiredKnowledgeChecks += required.filter(({ kc }) => {
        if (kc.id == null) return false
        return checks.some(
          (item) =>
            progressIsComplete(item) &&
            progressModuleId(item) != null &&
            progressKnowledgeCheckId(item) != null &&
            String(progressModuleId(item)) === String(module.id) &&
            String(progressKnowledgeCheckId(item)) === String(kc.id)
        )
      }).length
    }

    const totalItems = courseVideoIdSet.size + totalRequiredKnowledgeChecks
    if (!totalItems) return 0
    return ((completedVideosInCourse + completedRequiredKnowledgeChecks) / totalItems) * 100
  }

  if (!courseVideoIdSet.size) return 0
  return (completedVideosInCourse / courseVideoIdSet.size) * 100
}
